// Admin → Navigation: what's in the sidebar, in what order, and who sees each
// item. Changes are kept as a draft until Save, then written to SharePoint and
// applied to the sidebar straight away (everyone else gets them the next time
// they open the app).
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ExternalLink, Loader2, Lock, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useGraphToken } from "../auth/useGraphToken";
import { useNav } from "../nav/NavProvider";
import {
  AUDIENCES,
  BUILT_IN,
  NAV_ICONS,
  audienceText,
  canSee,
  iconFor,
  saveNavConfig,
  type NavAudience,
  type NavConfig,
  type NavEntry,
  type Viewer,
} from "../nav/navConfig";
import { ConfirmDialog } from "./ConfirmDialog";

type PreviewAs = "everyone" | "champion" | "admin";
const PREVIEW: { value: PreviewAs; label: string; viewer: Viewer }[] = [
  { value: "everyone", label: "Everyone", viewer: { email: "", isAdmin: false, isChampion: false } },
  { value: "champion", label: "Champion", viewer: { email: "", isAdmin: false, isChampion: true } },
  { value: "admin", label: "Admin", viewer: { email: "", isAdmin: true, isChampion: true } },
];

const sameConfig = (a: NavConfig, b: NavConfig) => JSON.stringify(a) === JSON.stringify(b);
const parsePeople = (text: string) =>
  Array.from(
    new Set(
      text
        .split(/[\s,;]+/)
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean)
    )
  );

/** What's wrong with the draft, if anything, in words. */
function problems(draft: NavConfig): string[] {
  const out: string[] = [];
  for (const e of draft) {
    const name = e.label.trim() || (e.builtIn ? e.path : "A new link");
    if (!e.label.trim()) out.push(`${name} needs a name.`);
    if (!e.builtIn && !/^https:\/\/\S+\.\S+/i.test(e.href ?? "")) out.push(`${name} needs a web address starting with https://.`);
    const bad = e.people.filter((p) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p));
    if (bad.length) out.push(`${name}: "${bad[0]}" isn't an email address.`);
  }
  return out;
}

export function NavigationAdmin() {
  const getToken = useGraphToken();
  const nav = useNav();
  const [draft, setDraft] = useState<NavConfig>(nav.config);
  const [open, setOpen] = useState<string | null>(null);
  const [previewAs, setPreviewAs] = useState<PreviewAs>("everyone");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Pick up the saved navigation once it loads, unless there are edits in progress.
  const [base, setBase] = useState(nav.config);
  useEffect(() => {
    if (sameConfig(nav.config, base)) return;
    setDraft((d) => (sameConfig(d, base) ? nav.config : d));
    setBase(nav.config);
  }, [nav.config, base]);

  const dirty = !sameConfig(draft, nav.config);
  const issues = useMemo(() => problems(draft), [draft]);

  const update = (id: string, patch: Partial<NavEntry>) => {
    setSaved(false);
    setDraft((d) => d.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };
  const move = (index: number, by: -1 | 1) => {
    setSaved(false);
    setDraft((d) => {
      const next = [...d];
      const [row] = next.splice(index, 1);
      next.splice(index + by, 0, row);
      return next;
    });
  };
  const remove = (id: string) => {
    setSaved(false);
    setDraft((d) => d.filter((e) => e.id !== id));
  };
  const addLink = () => {
    const id = `link-${Date.now().toString(36)}`;
    setSaved(false);
    setDraft((d) => [
      ...d,
      { id, label: "", icon: "link", href: "https://", onMenu: true, audience: "everyone", people: [], builtIn: false },
    ]);
    setOpen(id);
  };

  const run = async (label: string, next: NavConfig | null) => {
    setBusy(label);
    setError(null);
    try {
      const result = await saveNavConfig(await getToken(), next);
      nav.replace(result);
      setBase(result);
      setDraft(result);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const viewer = PREVIEW.find((p) => p.value === previewAs)!.viewer;
  const preview = draft.filter((e) => e.onMenu && canSee(e, viewer));

  return (
    <div className="bn-admin nv-admin">
      <div className="bn-admin-head">
        <div>
          <h2 className="bn-admin-title">Sidebar navigation</h2>
          <p className="bn-admin-sub">
            Choose what's in the sidebar, in what order, and who sees each item. A page hidden from someone is also closed
            to them by its address. Admins always see everything. Changes reach everyone the next time they open the app.
          </p>
        </div>
        <div className="bn-admin-actions">
          {busy ? (
            <span className="bn-note" role="status">
              <Loader2 size={14} className="add-item-spin" aria-hidden /> {busy}…
            </span>
          ) : saved && !dirty ? (
            <span className="bn-note" role="status">
              Saved.
            </span>
          ) : null}
          <button type="button" className="btn" onClick={addLink} disabled={Boolean(busy)}>
            <Plus size={15} aria-hidden /> Add link
          </button>
          {dirty ? (
            <button type="button" className="btn btn-ghost" onClick={() => setDraft(nav.config)} disabled={Boolean(busy)}>
              Discard
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void run("Saving", draft)}
            disabled={!dirty || issues.length > 0 || Boolean(busy)}
          >
            Save changes
          </button>
        </div>
      </div>

      {error ? (
        <div className="error-banner" role="alert">
          <strong>Couldn't save.</strong> {error}
        </div>
      ) : null}
      {dirty && issues.length ? (
        <p className="nv-issues" role="status">
          {issues[0]}
          {issues.length > 1 ? ` (and ${issues.length - 1} more)` : ""}
        </p>
      ) : null}

      <div className="nv-layout">
        <ol className="bn-list nv-list">
          {draft.map((e, i) => {
            const Icon = iconFor(e.icon);
            const expanded = open === e.id;
            const base = BUILT_IN.find((b) => b.id === e.id);
            return (
              <li key={e.id} className={`nv-item${e.onMenu ? "" : " is-muted"}${expanded ? " is-open" : ""}`}>
                <div className="bn-row nv-row">
                  <span className="bn-pos">{i + 1}</span>
                  <span className="nv-icon" aria-hidden>
                    <Icon size={18} strokeWidth={1.8} />
                  </span>
                  <span className="nv-body">
                    <input
                      className="nv-label-input"
                      type="text"
                      value={e.label}
                      maxLength={60}
                      placeholder={base?.label ?? "Link name"}
                      onChange={(ev) => update(e.id, { label: ev.target.value })}
                      aria-label={`Name for ${base?.label ?? "link"}`}
                    />
                    <span className="bn-row-meta nv-meta">
                      {e.href ? (
                        <>
                          <ExternalLink size={12} aria-hidden /> {e.builtIn ? "Opens " : ""}
                          {e.href.replace(/^https:\/\//i, "")}
                        </>
                      ) : (
                        <>Page · {e.path}</>
                      )}
                      {" · "}
                      {e.onMenu ? audienceText(e) : `Off the menu · opens for ${audienceText(e).toLowerCase()}`}
                    </span>
                  </span>
                  {e.locked ? (
                    <span className="nv-locked" title="Everyone keeps the dashboard; it's where the app sends people.">
                      <Lock size={13} aria-hidden /> Everyone
                    </span>
                  ) : (
                    <select
                      className="nv-select"
                      value={e.audience}
                      onChange={(ev) => update(e.id, { audience: ev.target.value as NavAudience })}
                      aria-label={`Who sees ${e.label || "this link"}`}
                      title={AUDIENCES.find((a) => a.value === e.audience)?.hint}
                    >
                      {AUDIENCES.map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <span className="bn-row-actions">
                    <label className="bn-switch bn-switch--compact" title={e.onMenu ? "In the sidebar" : "Not in the sidebar"}>
                      <input
                        type="checkbox"
                        checked={e.onMenu}
                        onChange={() => update(e.id, { onMenu: !e.onMenu })}
                        aria-label={`${e.label || "Link"} in the sidebar`}
                      />
                      <span className="bn-switch-track" aria-hidden />
                    </label>
                    <button type="button" className="bn-icon-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${e.label} up`}>
                      <ArrowUp size={15} />
                    </button>
                    <button
                      type="button"
                      className="bn-icon-btn"
                      onClick={() => move(i, 1)}
                      disabled={i === draft.length - 1}
                      aria-label={`Move ${e.label} down`}
                    >
                      <ArrowDown size={15} />
                    </button>
                    <button
                      type="button"
                      className="bn-icon-btn"
                      onClick={() => setOpen(expanded ? null : e.id)}
                      aria-expanded={expanded}
                      aria-label={`More settings for ${e.label || "this link"}`}
                    >
                      <ChevronDown size={15} className={expanded ? "is-flipped" : ""} />
                    </button>
                  </span>
                </div>

                {expanded ? (
                  <div className="nv-detail">
                    {!e.builtIn ? (
                      <label className="add-item-field">
                        <span className="add-item-label">Web address</span>
                        <input
                          type="url"
                          value={e.href ?? ""}
                          onChange={(ev) => update(e.id, { href: ev.target.value.trim() })}
                          placeholder="https://"
                        />
                        <span className="add-item-hint">Opens in a new tab.</span>
                      </label>
                    ) : null}
                    <div className="add-item-field">
                      <span className="add-item-label">Icon</span>
                      <div className="nv-icons" role="radiogroup" aria-label="Icon">
                        {Object.entries(NAV_ICONS).map(([key, { icon: I, label }]) => (
                          <button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={e.icon === key}
                            className={`nv-icon-pick${e.icon === key ? " is-active" : ""}`}
                            onClick={() => update(e.id, { icon: key })}
                            title={label}
                            aria-label={label}
                          >
                            <I size={16} strokeWidth={1.8} />
                          </button>
                        ))}
                      </div>
                    </div>
                    {e.locked ? null : (
                      <PeopleField
                        key={e.id}
                        people={e.people}
                        disabled={e.audience === "everyone"}
                        onChange={(people) => update(e.id, { people })}
                      />
                    )}
                    <div className="nv-detail-foot">
                      {base ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => update(e.id, { ...base, people: [] })}
                        >
                          <RotateCcw size={13} aria-hidden /> Back to this page's defaults
                        </button>
                      ) : (
                        <button type="button" className="btn btn-ghost btn-sm nv-danger" onClick={() => remove(e.id)}>
                          <Trash2 size={13} aria-hidden /> Remove this link
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>

        <aside className="nv-preview" aria-label="Sidebar preview">
          <div className="nv-preview-head">
            <span className="add-item-label">Preview as</span>
            <div className="bn-seg" role="radiogroup" aria-label="Preview as">
              {PREVIEW.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={previewAs === p.value}
                  className={`bn-seg-btn${previewAs === p.value ? " is-active" : ""}`}
                  onClick={() => setPreviewAs(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <ul className="nv-preview-list">
            {preview.map((e) => {
              const Icon = iconFor(e.icon);
              return (
                <li key={e.id}>
                  <Icon size={16} strokeWidth={1.8} aria-hidden />
                  <span>{e.label || "Untitled link"}</span>
                  {e.href ? <ExternalLink size={12} aria-hidden className="nv-preview-ext" /> : null}
                </li>
              );
            })}
          </ul>
          <p className="add-item-hint">
            {previewAs === "everyone"
              ? "People named on an item see it too, whatever their role."
              : previewAs === "champion"
                ? "Anyone with the Champion role, plus people named on an item."
                : "Admins see every item, including ones off the menu by address."}
          </p>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setConfirmReset(true)}
            disabled={Boolean(busy)}
          >
            <RotateCcw size={13} aria-hidden /> Reset everything to defaults
          </button>
        </aside>
      </div>

      <ConfirmDialog
        open={confirmReset}
        title="Reset the navigation?"
        message={
          <p>
            The sidebar goes back to the app's own pages, names, order and audiences. Links you added are removed.
          </p>
        }
        confirmLabel="Reset"
        tone="danger"
        busy={busy === "Resetting"}
        onConfirm={async () => {
          await run("Resetting", null);
          setConfirmReset(false);
        }}
        onCancel={() => (busy ? null : setConfirmReset(false))}
      />
    </div>
  );
}

/** Extra people, by email, typed freely and tidied up when the field loses focus. */
function PeopleField({
  people,
  disabled,
  onChange,
}: {
  people: string[];
  disabled: boolean;
  onChange: (people: string[]) => void;
}) {
  const [text, setText] = useState(people.join(", "));
  useEffect(() => setText(people.join(", ")), [people]);
  return (
    <label className="add-item-field">
      <span className="add-item-label">Also visible to</span>
      <textarea
        rows={2}
        value={text}
        disabled={disabled}
        placeholder={disabled ? "Everyone already sees this." : "name@brookfieldrp.com, another@brookfieldrp.com"}
        onChange={(ev) => setText(ev.target.value)}
        onBlur={() => onChange(parsePeople(text))}
      />
      <span className="add-item-hint">
        {disabled
          ? "Choose Champions or Admins only above to limit it, then add people here."
          : "Email addresses, separated by commas. They see it whatever their role."}
      </span>
    </label>
  );
}
